import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { esAdmin } from '@/lib/roles';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  Truck, CheckCircle2, XCircle, DollarSign,
  Package, Search, Loader2, AlertTriangle, Receipt, Settings
} from 'lucide-react';
import AddressBookPicker from '@/components/AddressBookPicker';
import SelleGardCreditCard from '@/components/SelleGardCreditCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SelleGard Freight — merchant-facing shipping page.
 * White-labeled. No third-party carrier brand exposure. No credential form.
 * Admins get a link to the admin config page in the header.
 */
export default function WebShipPage() {
  const { token, user } = useAuth();
  const isAdmin = esAdmin(user);
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);

  // Quote form
  const [qOriginZip, setQOriginZip] = useState('');
  const [qDestZip, setQDestZip] = useState('');
  const [qWeight, setQWeight] = useState('1');
  const [qLen, setQLen] = useState('8');
  const [qWid, setQWid] = useState('6');
  const [qHei, setQHei] = useState('3');
  const [qResidential, setQResidential] = useState(true);
  const [quotes, setQuotes] = useState(null);
  const [quoting, setQuoting] = useState(false);

  // Billing + shipments
  const [ledger, setLedger] = useState(null);
  const [weekly, setWeekly] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/webship/status`, { headers: auth });
      const s = await r.json();
      setStatus(s);
      if (s.connected) {
        const [led, wk] = await Promise.all([
          fetch(`${API}/webship/billing/ledger?days=30`, { headers: auth }).then((r) => r.json()),
          fetch(`${API}/webship/billing/weekly-summary`, { headers: auth }).then((r) => r.json()),
        ]);
        setLedger(led);
        setWeekly(wk);
      }
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const doQuote = async () => {
    setQuoting(true); setBanner(null); setQuotes(null);
    try {
      const r = await fetch(`${API}/webship/quote`, {
        method: 'POST', headers: auth,
        body: JSON.stringify({
          sender: { country: 'US', zip: qOriginZip },
          receiver: { country: 'US', zip: qDestZip },
          residential: qResidential,
          contentDescription: 'sample package',
          weightUnit: 'lb', dimUnit: 'in', currency: 'USD', customsCurrency: 'USD',
          pieces: [{
            weight: qWeight, length: qLen, width: qWid, height: qHei,
            insuranceAmount: null, declaredValue: null,
          }],
          billing: { party: 'sender' },
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Quote failed');
      setQuotes(d.quotes || [d]);
    } catch (e) { setBanner({ type: 'err', text: e.message }); }
    finally { setQuoting(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
  }

  const connected = status?.connected;

  return (
    <div className="space-y-6" data-testid="sellegard-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">SelleGard Freight</h1>
            <p className="text-sm text-slate-500">
              Discounted domestic + international rates · labels · on credit, invoiced weekly.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <Link to="/dashboard/admin/sellegard-config">
              <Button variant="outline" size="sm" data-testid="admin-config-btn">
                <Settings className="w-3.5 h-3.5 mr-1.5" /> Admin config
              </Button>
            </Link>
          )}
          {isAdmin && (
            <Link to="/dashboard/admin/sellegard-credit">
              <Button variant="outline" size="sm" data-testid="admin-credit-btn">
                <Receipt className="w-3.5 h-3.5 mr-1.5" /> Merchant credit
              </Button>
            </Link>
          )}
        </div>
      </div>

      {status?.reference_code && (
        <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 text-sm" data-testid="sellegard-reference">
          Your SelleSend reference: <b className="font-mono">{status.reference_code}</b>.
          {' '}It goes on every label you book here. If you book directly in WebShip,
          type it in the shipment reference field so the shipment reaches your account.
        </div>
      )}

      {banner && (
        <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${banner.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-testid="sellegard-banner">
          {banner.type === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
          <span>{banner.text}</span>
        </div>
      )}

      {/* STATUS CARD — never shows credentials to merchants */}
      {!connected ? (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-5 flex items-start gap-3" data-testid="not-connected-card">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-semibold">SelleGard Freight is temporarily unavailable.</p>
            <p className="text-sm text-slate-600 mt-1">
              Our shipping partner is being configured by SelleSend administrators. Please try again shortly, or contact support if you need to ship right away.
            </p>
            {isAdmin && (
              <Link to="/dashboard/admin/sellegard-config">
                <Button size="sm" className="mt-3" data-testid="go-configure-btn">
                  <Settings className="w-4 h-4 mr-1.5" /> Configure now
                </Button>
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 flex items-center justify-between" data-testid="connected-card">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="font-medium text-emerald-900 dark:text-emerald-200">SelleGard is live · {status.services_count} services available</p>
              {/* Decia que el seguro se aplicaba solo por RiskChoice, con un boton
                  a esa pantalla: la reserva manda insuranceAmount en null y
                  Programs esta fuera del menu (§9.121, §9.159). */}
              <p className="text-xs text-slate-500">Rates and labels ship through your SelleSend account.</p>
            </div>
          </div>
        </div>
      )}

      {/* Credit: limit, what is owed, weekly invoices. Shown even when the carrier
          is not connected, because an open invoice still has to be payable. */}
      <SelleGardCreditCard token={token} />

      {/* Billing snapshot — visible on every shipping page */}
      {connected && weekly && (
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-5" data-testid="weekly-billing">
          <div className="flex items-center gap-2 mb-3">
            <Receipt className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold">This week&apos;s rebill (last 7 days)</h3>
          </div>
          <div className={`grid grid-cols-2 ${isAdmin ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4`}>
            <div><p className="text-xs uppercase tracking-wide text-slate-500">Shipments</p><p className="text-2xl font-bold tabular-nums">{weekly.shipments}</p></div>
            {/* El comerciante ve lo que paga por el envio, no nuestro costo y el margen (Mike, 12-oct).
                Desde la revision del 26-oct el servidor ya no se los manda. */}
            {isAdmin ? (
              <>
                <div><p className="text-xs uppercase tracking-wide text-slate-500">Carrier cost</p><p className="text-2xl font-bold tabular-nums">${(weekly.quoted_total || 0).toFixed(2)}</p></div>
                <div><p className="text-xs uppercase tracking-wide text-slate-500">Our markup</p><p className="text-2xl font-bold tabular-nums text-emerald-600">${(weekly.markup_total || 0).toFixed(2)}</p></div>
              </>
            ) : (
              <div><p className="text-xs uppercase tracking-wide text-slate-500">Shipping</p><p className="text-2xl font-bold tabular-nums">${(weekly.shipping_total || 0).toFixed(2)}</p></div>
            )}
            <div><p className="text-xs uppercase tracking-wide text-slate-500">Admin fees</p><p className="text-2xl font-bold tabular-nums">${(weekly.admin_fee_total || 0).toFixed(2)}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-slate-500">Billable total</p><p className="text-2xl font-bold tabular-nums text-amber-700">${weekly.billable_total.toFixed(2)}</p></div>
          </div>
        </div>
      )}

      {/* Quote panel */}
      {connected && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6" data-testid="quote-card">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-amber-500" />
            <h3 className="text-lg font-semibold">Get a shipping quote</h3>
          </div>
          {/* Address book pickers — one-click origin/destination */}
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <AddressBookPicker
              token={token}
              type="ship_from"
              value={{ zip: qOriginZip }}
              onSelect={(a) => setQOriginZip(a.zip || '')}
              testid="sg-from-addrbook"
              label="From address"
            />
            <AddressBookPicker
              token={token}
              type="ship_to"
              value={{ zip: qDestZip }}
              onSelect={(a) => setQDestZip(a.zip || '')}
              testid="sg-to-addrbook"
              label="To address"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Field label="From ZIP"><input value={qOriginZip} onChange={(e) => setQOriginZip(e.target.value)} placeholder="84117" className="input" data-testid="q-from" /></Field>
            <Field label="To ZIP"><input value={qDestZip} onChange={(e) => setQDestZip(e.target.value)} placeholder="84106" className="input" data-testid="q-to" /></Field>
            <Field label="Weight (lb)"><input value={qWeight} onChange={(e) => setQWeight(e.target.value)} className="input" data-testid="q-weight" /></Field>
            <Field label="L (in)"><input value={qLen} onChange={(e) => setQLen(e.target.value)} className="input" data-testid="q-len" /></Field>
            <Field label="W (in)"><input value={qWid} onChange={(e) => setQWid(e.target.value)} className="input" data-testid="q-wid" /></Field>
            <Field label="H (in)"><input value={qHei} onChange={(e) => setQHei(e.target.value)} className="input" data-testid="q-hei" /></Field>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={qResidential} onChange={(e) => setQResidential(e.target.checked)} data-testid="q-residential" />
              Residential
            </label>
            <Button onClick={doQuote} disabled={quoting || !qOriginZip || !qDestZip} className="bg-gradient-to-r from-amber-500 to-orange-600" data-testid="quote-btn">
              {quoting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Quoting…</> : <><DollarSign className="w-4 h-4 mr-2" />Get rates</>}
            </Button>
            <Link to="/dashboard/rate-shop">
              <Button variant="outline" data-testid="fullshop-btn"><Package className="w-4 h-4 mr-2" />Full rate shop</Button>
            </Link>
          </div>

          {quotes && (
            // En el celular las tablas se desplazan dentro de su recuadro (502 px en 375, 29-oct).
            <div className="mt-5 rounded-xl border border-slate-200 dark:border-slate-800 overflow-x-auto" data-testid="quotes-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900/60 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Carrier</th>
                    <th className="px-4 py-2 text-left">Service</th>
                    {isAdmin && <th className="px-4 py-2 text-right">Carrier price</th>}
                    {isAdmin && <th className="px-4 py-2 text-right">Markup</th>}
                    {!isAdmin && <th className="px-4 py-2 text-right">Shipping</th>}
                    <th className="px-4 py-2 text-right">Admin fee</th>
                    <th className="px-4 py-2 text-right">Your price</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((q, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-4 py-2 uppercase text-xs font-semibold">{q.carrierCode}</td>
                      <td className="px-4 py-2">{q.serviceDescription || q.serviceCode}</td>
                      {isAdmin && <td className="px-4 py-2 text-right tabular-nums">${parseFloat(q.totalAmount || 0).toFixed(2)}</td>}
                      {isAdmin && <td className="px-4 py-2 text-right tabular-nums text-emerald-600">+${(q.marketplaceMarkup || 0).toFixed(2)}</td>}
                      {!isAdmin && <td className="px-4 py-2 text-right tabular-nums">${(q.shippingAmount || 0).toFixed(2)}</td>}
                      <td className="px-4 py-2 text-right tabular-nums">+${(q.adminFee || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-bold">${q.finalAmount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Ledger */}
      {connected && ledger && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6" data-testid="ledger-card">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-5 h-5 text-slate-500" />
            <h3 className="text-lg font-semibold">Shipment ledger · last {ledger.days} days</h3>
          </div>
          {ledger.records.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No shipments yet. Buy your first label at <Link to="/dashboard/rate-shop" className="text-amber-600 underline">Rate Shop</Link>.</p>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-2 text-left">Book #</th>
                  <th className="px-2 py-2 text-left">Order</th>
                  <th className="px-2 py-2 text-left">Carrier</th>
                  {isAdmin && <th className="px-2 py-2 text-right">Carrier</th>}
                  {isAdmin && <th className="px-2 py-2 text-right">Markup</th>}
                  {!isAdmin && <th className="px-2 py-2 text-right">Shipping</th>}
                  <th className="px-2 py-2 text-right">Admin fee</th>
                  <th className="px-2 py-2 text-right">Billed</th>
                </tr>
              </thead>
              <tbody>
                {ledger.records.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`ledger-row-${r.book_number}`}>
                    <td className="px-2 py-2 font-mono text-xs">{r.book_number}</td>
                    <td className="px-2 py-2 text-xs">{r.order_id || '—'}</td>
                    <td className="px-2 py-2 uppercase text-xs">{r.carrier_code}</td>
                    {isAdmin && <td className="px-2 py-2 text-right tabular-nums">${(r.quoted_amount || 0).toFixed(2)}</td>}
                    {isAdmin && <td className="px-2 py-2 text-right tabular-nums text-emerald-600">+${(r.markup_amount || 0).toFixed(2)}</td>}
                    {!isAdmin && <td className="px-2 py-2 text-right tabular-nums">${(r.shipping_amount || 0).toFixed(2)}</td>}
                    <td className="px-2 py-2 text-right tabular-nums">+${(r.admin_fee_amount || 0).toFixed(2)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-semibold">${r.billed_amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      <style>{`.input{width:100%;height:38px;padding:0 10px;border-radius:8px;border:1px solid rgb(226 232 240);font-size:13px;background:transparent;}
        .dark .input{border-color:rgb(51 65 85);}`}</style>
    </div>
  );
}

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="text-xs uppercase tracking-wide text-slate-500 block mb-1">{label}</label>
    {children}
  </div>
);
